// src/store/helpers/functions/onRequest.ts

import { useApplicationStore } from "@/store/application.store";

// Base URL for Firebase functions (adjust based on your project)
const FUNCTIONS_BASE_URL = 'https://us-central1-shirly-2413a.cloudfunctions.net'

// Optional: Define a type for streaming responses if needed
export interface StreamedFunctionResponse {
    onData: (data: any) => void;
    onError: (error: any) => void;
    onComplete: () => void;
}

/**
 * Calls a Firebase onRequest function with streaming support.
 * @param functionName - The name of the Firebase function to call.
 * @param payload - The payload to send to the function.
 * @param streamedResponse - Callbacks for handling streamed data, errors, and completion.
 */
export async function callOnRequestFunctionStream(
    functionName: string,
    payload: any,
    streamedResponse: StreamedFunctionResponse
): Promise<void> {
    const applicationStore = useApplicationStore();

    try {
        const response = await fetch(`${FUNCTIONS_BASE_URL}/${functionName}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Unknown error occurred');
        }

        if (!response.body) {
            throw new Error('ReadableStream not supported in this environment.');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
                const chunk = buffer.slice(0, boundary);
                buffer = buffer.slice(boundary + 2);

                if (chunk.startsWith('data: ')) {
                    const jsonData = chunk.replace('data: ', '');
                    try {
                        const data = JSON.parse(jsonData);
                        streamedResponse.onData(data);
                    } catch (e) {
                        console.error('Failed to parse streamed JSON:', e);
                        streamedResponse.onError(e);
                    }
                }

                boundary = buffer.indexOf('\n\n');
            }
        }

        streamedResponse.onComplete();
    } catch (error: any) {
        console.error(`Error calling function ${functionName}:`, error);
        streamedResponse.onError(error);

        // Optionally, show a notification
        applicationStore.showNotification({
            title: 'Error',
            message: error.message || 'An error occurred',
            isOpen: false
        });
    }
}